/**
 * The workspace client's string catalog - and the enforcement point for the LANGUAGE LAW
 * (workspace-client.md §2g).
 *
 * The law: no protocol identifier and no mechanics term may appear in anything a player reads. "a
 * decision is due" is "we need an answer by Thursday"; "check in" is "wrapping up"; "resolve the active
 * event" is "reply on the incident". The wire stays semantic and internal (§3); the RENDERED string is
 * always something a colleague could have said.
 *
 * It is enforced HERE, and only here, because every rendered string already passes through this file -
 * which makes the law a test (`i18n.test.ts` scans both catalogs) rather than a habit. A string built by
 * concatenation somewhere in the render layer would escape it, so the render layer takes keys, not text.
 *
 * The shape follows the terminal's catalog on purpose (dotted keys grouped by surface, `{name}`
 * interpolation, English as the fallback, RU asserted complete): one language per session, a few hundred
 * short strings, and nothing here worth a dependency.
 */
export type Lang = 'en' | 'ru';

/** The English catalog. It is also the key list: RU is checked against it in tests. */
const EN = {
  // ---- the workspace chrome ----------------------------------------------------------------------
  'app.title': 'Team Simulator',
  'app.workspaceFallback': 'the team',
  'app.channels': 'Channels',
  'app.people': 'People',
  'app.pages': 'Pages',
  'app.you': 'you',
  'app.signIn': 'Join the team',
  'app.signInName': 'What should the team call you?',
  'app.signInGo': 'Come in',
  'app.signingIn': 'One moment…',
  'app.entryTitle': 'Team Simulator',
  // DEC-18: your teams first; the directory is opt-in and usually empty.
  'app.myWorkspaces': 'Your teams',
  'app.openWorkspaces': 'Teams you can walk into',
  'app.noWorkspaces': 'No teams yet. Start one below — you will get a link to invite people.',
  'app.newWorkspace': 'Start a new team',
  'app.newWorkspaceName': 'Name the team',
  // DEC-17 M4 increment 8b: this client hard-coded one deck, so four shipped ones were unreachable.
  'app.newWorkspaceDeck': 'What are you walking into',
  // The two FRAMES read as different offers, because they are: an excerpt drops you into a situation
  // already underway, a sim hands you a company to run until it stops.
  'app.deckExcerpt': '{title} — a situation already underway, about {days} days',
  'app.deckSim': '{title} — a company to run, for as long as you can keep it',
  'app.enter': 'Open',
  'app.reconnecting': 'Reconnecting — you can read, but nothing you type will send yet.',
  'app.offlineNote': 'Nothing to do: it comes back on its own.',
  'app.leave': 'Step out',
  'app.problemSend': 'That did not go out. Say it again once the connection is back.',
  // The outbox (stage one): your own line, on screen before the server has confirmed it.
  'outbox.sending': 'Sending…',
  'outbox.failed': 'Did not send.',
  'outbox.retry': 'Send again',
  'outbox.discard': 'Discard',
  'app.problemJoin':
    'Could not get you in: {error}. Try the list again, or ask whoever invited you for the link.',
  'app.problemCreate': 'Could not start that team: {error}. Try again in a moment.',
  'app.problemSignIn': 'Could not sign you in: {error}. Try again.',
  // The refresh cookie went stale mid-session. There IS a move and it is the same one either way, so
  // the line says it rather than interpolating an empty cause into a sentence about a cause.
  'app.problemSignedOut': 'You have been signed out. Give your name again to come back in.',
  // standards/messages.md §2.4: "try again" is the right shape and the wrong advice while a limiter is
  // holding the door - trying again now fails again, and the player concludes the software is broken.
  'app.problemWait': 'Too many tries at once — wait {seconds}s and go again.',
  // The standing line for somebody with no role (DEC-19). Two situations, and only one has a move.
  // Identity hygiene (playtest one): three guests left behind in one session, two named after text
  // somebody typed while working out how to drive this. Diegetic, and both name the kinder move.
  'app.nameLooksLikeCommand':
    'Careful - "{name}" reads like something you were trying to do, and it would become your name to the whole team.',
  'app.identityExists':
    'You already have a name in this browser, in {teams} team(s). A new one starts over and cannot get their roles back. Leave the box empty to come back as yourself.',
  'app.identityConfirm': 'Press "Come in" again to use this name anyway.',
  'room.pickRole':
    'You have not taken a role yet, so nobody can act for you and colleagues cannot answer you. Pick one in the people list on the right.',
  'room.watching':
    'You are watching this room. You can read everything and talk here; you cannot take actions, and colleagues cannot answer you. A role opens up when somebody leaves.',
  // The server states the fact; the client names the move in its own vocabulary (messages.md §4).
  // The three targeted refusals, written HERE rather than shown as the server sent them.
  //
  // Not a translation layer for its own sake: the server's sentence is written in the vocabulary the
  // terminal shares with it, and §2g's law is that this workplace has people with jobs and not seats.
  // messages.md §4 puts the action in the client for the same reason - a role is CLICKED here and
  // typed over there - so the code travels and the sentence is local.
  'app.noticeNoRole':
    'Nobody can answer you yet: you have not taken a role. Pick one in the people list on the right, and it is yours.',
  'app.noticeNoRoleFree':
    'Nobody can answer you yet: you have not taken a role. Free right now: {roles}. Pick one in the people list on the right.',
  'app.noticeNoRoleFull':
    'Nobody can answer you yet: you have not taken a role, and every job here is taken. Say it to the room instead, or open a room of your own.',
  'app.noticeSeated': 'You are in as {role}.',
  'app.noticeObserver':
    'No role was free, so you are watching this room. You can read everything and talk here; you cannot take actions. Open a room of your own to play.',
  'app.noticeUnknownName': 'Nobody here goes by that name. In this channel you can reach: {names}.',
  'app.noticeUnknownNameNobody':
    'Nobody here goes by that name, and nobody in this channel answers being called out. Try the team channel.',
  'app.noticeTooFast':
    'You are calling on people faster than they can answer. Wait {seconds}s and ask again.',
  'app.problemAction': '{message}',
  'app.problemRefused': 'That did not go through: {message}',

  // ---- the lobby, before day one (§2l) -----------------------------------------------------------
  'lobby.title': 'Before the first morning',
  'lobby.subtitle':
    'People are still arriving. Say hello, and take a role when you see one you want.',
  'lobby.roleOpen': 'This role is open',
  'lobby.roleTake': 'Take it',
  'lobby.roleTaken': '{name} has this',
  'lobby.roleLocked': 'This one needs someone who has shipped here before',
  'lobby.roleDoes': 'Does: {powers}',
  'lobby.start': 'Start the day',
  'lobby.startHint': 'Whoever opened the room starts it. Everyone else can carry on talking.',
  'lobby.waiting': 'Waiting on the room',
  'lobby.quiet': '{name} is here but has not said anything yet',
  'lobby.ready': 'here',

  // ---- composer truth, per channel (§1a) ---------------------------------------------------------
  'composer.team': 'Say anything',
  'composer.dm': 'Ask {name} anything',
  'composer.warRoom': 'Reply here, or use the buttons on the incident',
  'composer.readOnly': 'You are reading. Take a role to join in.',
  'composer.send': 'Send',
  // Who you can mention (§3.5). A mention is addressed to a SEAT and the server parses the key, so a
  // player typing one from memory has to know a word this client never showed them.
  'mention.label': 'Who you can mention',
  'mention.none': 'Nobody here matches “{prefix}”',

  // ---- the channels ------------------------------------------------------------------------------
  'channel.team': '#team',
  'channel.teamPurpose': 'The team. Mood, banter, and who is on what.',
  'channel.warRoom': '#war-room',
  'channel.warRoomSev': '#war-room · sev{n}',
  'channel.warRoomPurpose': 'Where the fire is fought.',
  'channel.dm': '{name}',

  // ---- the topic line, which is the goal readout (§2d) -------------------------------------------
  'topic.goalGood': 'Done when: {win}',
  'topic.goalHold': 'Done when: {win} · holding: {lose}',
  'topic.goalBreached': '⚠ {lose} — that is the line we said we would not cross',
  'topic.deadlineToday': 'Today is the last day.',
  'topic.deadlineDays': '{n} working day(s) left.',
  'topic.noDeadline': 'No date has been set.',
  'topic.clauseAbove': '{metric} at {current}, we need {target}',
  'topic.clauseBelow': '{metric} at {current}, keep it over {target}',
  'topic.clauseUnder': '{metric} at {current}, needs to be under {target}',
  'topic.clauseKeepUnder': '{metric} at {current}, keep it under {target}',
  'topic.clauseAt': '{metric} at {current}, needs to be {target}',
  'topic.and': ' and ',
  'topic.or': ' or ',

  // ---- the cast's plain-language names for the meters (§2g: no mechanics words) -------------------
  'metric.stability': 'the platform',
  'metric.sev': 'the severity',
  'metric.trust': 'the customer',
  'metric.morale': 'the team',
  'metric.quality': 'quality',
  'metric.scope': 'scope',

  // ---- the pages, one fiction per meter (§2o) ----------------------------------------------------
  'page.monitoring': 'Monitoring',
  'page.monitoringLead': 'What the graphs say right now.',
  'page.monitoringStability': 'Platform health',
  'page.monitoringSev': 'Severity',
  // DEC-17 M2: a consequence already queued, waiting on a condition rather than a countdown.
  'page.monitoringArmed': 'Watch: {metric}',
  'page.monitoringArmedUnknown': 'something is queued against this',
  'page.pulse': 'Pulse survey',
  'page.pulseLead': 'The two taps everyone does at the end of the day. This is what came back.',
  'page.pulseScore': 'How the team says it is doing',
  'page.pulseParticipation': 'Answered today: {done} of {total}',
  'page.accounts': 'Account health',
  'page.accountsLead': 'How the customer is feeling about us.',
  'page.accountsCustomer': 'Acme',
  'page.accountAtRisk': 'AT RISK',
  'page.accountWatch': 'WATCH',
  'page.accountHealthy': 'HEALTHY',
  'page.rebuildingSlow': 'Rebuilding: slow. It always is.',
  'page.empty': 'Nothing on this page yet.',

  // ---- the medium's grammar (§2m) ----------------------------------------------------------------
  'gesture.signOff': 'Wrapping up',
  'gesture.signOffHint': 'Posts your line, greys you out, and ends your day.',
  'gesture.signOffLine': 'Wrapping up for today.',
  'gesture.signedOff': 'Signed off',
  'gesture.moodAsk': 'Before you go — how was it?',
  'gesture.moodYou': 'You',
  'gesture.moodTeam': 'The team',
  'gesture.moodGood': 'Good',
  'gesture.moodFlat': 'Flat',
  'gesture.moodRough': 'Rough',
  'gesture.moodSkip': 'Rather not say',
  'presence.here': 'here',
  'presence.away': 'away',
  'presence.gone': 'signed off',
  'time.hover': 'Really {real}',
  'day.divider': '{day}',
  'day.today': '{day} · today',
  'day.names.1': 'Monday',
  'day.names.2': 'Tuesday',
  'day.names.3': 'Wednesday',
  'day.names.4': 'Thursday',
  'day.names.5': 'Friday',
  'day.week': 'week {n}',

  // ---- the incident, as the monitoring bot tells it (§2n) ----------------------------------------
  'incident.sender': 'monitoring',
  'incident.headline': '{text}',
  'incident.onlyYou': 'Only you can see this: {reveal}',
  'incident.pick': 'What do you want to do?',
  'incident.bet': 'A gamble — {odds}',
  'incident.unlabelled': 'the other thing',
  'incident.caused': 'This one follows from a call the team already made.',
  'incident.scripted': 'This one was always coming.',
  'incident.needRole': 'You are reading this one. Take a role and you can answer it.',
  'confirm.title': 'Before you answer',
  'confirm.option': 'You are choosing: {label}',
  'confirm.moves': 'What this is measured against:',
  'confirm.why': 'Say why, in one line — the room will read it back later',
  'confirm.whyPlaceholder': 'because…',
  'confirm.whyRequired': 'One line first. This is the kind of call people ask about afterwards.',
  'confirm.go': 'Do it',
  'confirm.cancel': 'Not yet',

  // ---- your role's own verbs, as composer actions (§2 seat verbs) --------------------------------
  'verb.title': 'Yours to do',
  'verb.declare-incident': 'Declare an incident',
  'verb.talk-to-stakeholder': 'Call the customer',
  'verb.investigate': 'Dig into it',
  'verb.mitigate': 'Put a lid on it',
  'verb.fix': 'Fix it properly',
  'verb.update-status': 'Post a status update',
  'verb.generic': '{verb}',
  'verb.done': 'Done: {label}',

  // ---- the member list (§1b: colleagues, never vacancies) ----------------------------------------
  'member.you': '{name} (you)',
  'member.openRole': 'Open role',
  'member.take': 'Take this role',
  // DEC-16: the deck's own recommendation for somebody who has never played it.
  'member.takeRecommended': 'Take this role — start here',
  'member.leaving': 'Give up this role',

  // ---- the onboarding buddy (§1a) — scripted, before it is ever a model call ----------------------
  'dm.heard': '“{quote}” — yes.',
  'dm.scripted': 'scripted',
  'empty.dmColleague': 'Ask {name} something. They can only speak to what they actually know.',
  'dm.deflect':
    "Sorry — I can't help with that one. Ask me how I'm doing, or about a call on the table.",
  'dm.thinking': 'typing…',
  'channel.decisions': '#decisions',
  'channel.decisionsPurpose': 'Calls waiting on this team. Every one of them has an owner.',
  'empty.decisions': 'Nothing is waiting on a decision right now.',
  'composer.decisions': 'Say something about these calls',
  'decision.card': '{kind} — I need an answer on this by day {day}.',
  'decision.style.OwnerDecides': 'Mine to call. Tell me if you disagree.',
  'decision.style.ConsultThenDecide': 'I want {n} of you to weigh in before I call it.',
  'decision.style.Committee': 'We vote. {votes}/{quorum} in so far.',
  'decision.style.ApprovalChain': 'Needs {approver} to sign it off.',
  'decision.vote': 'Vote {option}',
  'decision.decide': 'Go with {option}',
  'decision.approve': 'Sign it off',
  'decision.defer': 'Not today',
  'decision.deferredBefore': 'Put off {n} time(s) already.',
  'promise.offer': 'Promise {who} something',
  'promise.title': 'What are you promising?',
  'promise.what': 'What you will deliver',
  'promise.whatPlaceholder': 'the SLA dashboard',
  'promise.by': 'By which day',
  'promise.go': 'Give my word',
  'promise.needWhat': 'Say what you are promising — they will hold you to the words.',
  'finale.wonPlain': 'You shipped it.',
  'finale.lostPlain': 'The team ran out of road.',
  // DEC-17 M4: the endless frame's ending. A company history, not a scoreboard.
  'finale.collapsedPlain': 'The company did not survive.',
  'finale.survival': 'How far it got',
  'finale.survivalLasted': 'It lasted {days} days.',
  'finale.survivalGates': 'It reached {gates} milestones.',
  'finale.survivalPeak': 'At its best it was worth {peak}. At the end, {final}.',
  'finale.theRoom': 'the room',
  'finale.pathClock': 'nobody called it - the clock did',
  'finale.pathOverrode': 'called over {n} objection(s)',
  'finale.pathDeferred': 'put off {n} time(s) first',
  'finale.allHands': 'All-hands',
  'finale.open': 'Open the all-hands',
  'finale.close': 'Back to the office',
  'finale.next': 'Next',
  'finale.back': 'Back',
  'finale.story': 'What happened',
  'finale.calls': 'The calls you made',
  'finale.letter': 'For you',
  // §2g: the badge must be honest WITHOUT naming machinery. "Composed from the engine's own
  // record" is what the terminal says, and the language-law test correctly refused it here.
  'finale.scripted': 'Assembled from the notes, not written up.',
  'finale.share': 'Copy this for sharing',
  'finale.shared': 'Copied.',
  'finale.score': 'Outcome {score}',
  'finale.after': 'The day is over. The office is still here to read.',
  'channel.random': '#random',
  'channel.randomPurpose': 'Not work. Mostly.',
  'composer.random': 'Say something off the clock',
  'empty.random': 'Quiet in here.',
  'cast.outsideWorld': 'Outside the team',
  'react.add': '+',
  'react.addHint': 'React to this',
  'buddy.name': 'Nour Amrani',
  'buddy.role': 'Developer experience',
  'buddy.line1':
    'Hey — welcome. You have landed in {workspace} on a bad morning: something is on fire.',
  'buddy.line2':
    'Talking here is real. Type into any channel, DM anyone, ask me whatever you like — it all lands.',
  'buddy.line3':
    'Decisions are different. They live on the buttons under the post that raised them, not in a sentence you type at me.',
  'buddy.line4': 'Your first move: your role is open in the list on the right. Take it.',
  'buddy.line4Seated': 'You already have a role, so you are set. Go say hello in #team.',
  'buddy.started': 'Right — that is the day started. Watch #war-room.',
  'buddy.proseCommand':
    'You can say that, but saying it does not do it — that call lives on the incident in #war-room, on the buttons under it.',
  'buddy.proseCommandIdle':
    'You can say that here, but nothing is waiting on that decision right now. When something is, it turns up in #war-room with buttons on it.',
  'buddy.unknown':
    'I can tell you where things live: talk in the channels, decide on the buttons, and your role is in the list on the right.',

  // ---- what a message looks like when nobody has said anything -----------------------------------
  'empty.team': 'Nobody has said anything yet. You could.',
  'empty.warRoom': 'Quiet. It will not stay that way.',
  'empty.dm': 'Just the two of you.',

  // ---- the roster and system beats ---------------------------------------------------------------
  'beat.joined': '{name} joined the team.',
  'beat.tookRole': '{name} is on {role} now.',
  'beat.dayOpen': 'Morning. {day}.',
  'beat.signedOff': '{name} is wrapping up.',
  'beat.narrator': 'the room',

  // ---- the decomposed narrator (§2n): apps a workspace actually contains, none of them omniscient ----
  'bot.meeting': 'meeting notes',
  'bot.deploy': 'deploys',
  'bot.digest': 'the daily',
  'bot.survey': 'the survey',

  // ---- the standup, as a thread that opens itself (§2) ---------------------------------------------
  'channel.standup': '#standup',
  'channel.standupPurpose': 'The meeting, while it is happening.',
  'channel.standupOffered': 'On the calendar. Nobody has taken it yet.',
  'composer.standup': 'Reply to take part',
  'empty.standup': 'No meeting right now.',
  'scene.standup': 'the standup',
  'scene.planning': 'planning',
  'scene.governance': 'the governance meeting',
  'scene.retro': 'the retro',
  'standup.opened': '{scene} is on the calendar. Reply when you are ready.',
  'standup.running': '{scene}, in progress.',
  'standup.take': 'Join in',
  'standup.next': 'Move on',
  'standup.beatOf': 'part {index} of {count}',
  'standup.beatDigest': 'What happened since last time.',
  'standup.beatReveals': 'Anything the rest of us should know?',
  'standup.beatAgenda': 'What needs an answer today.',
  'standup.beatCommitments': 'Who is picking up what.',
  'standup.beatClose': 'That is us. How was it?',
  'standup.digestQuiet': 'Nothing happened since last time.',
  'standup.digestEvents': '{n} thing(s) dealt with',
  'standup.digestDecisions': '{n} call(s) made',
  'standup.digestTimedOut': '{n} call(s) ran out of time and went the worst way',
  'standup.digestWork': '{n} thing(s) shipped',
  'standup.digestFuses': '{n} thing(s) we set up earlier landed',
  'standup.digestEscalated': '{n} worry(s) got loud',
  'standup.digestPromises': '{n} promise(s) broken',
  'standup.digestHires': '{n} new person(s) started',

  // ---- the calendar strip (§2a): what is coming, never what is not --------------------------------
  'calendar.today': '{scene} today',
  'calendar.tomorrow': '{scene} tomorrow morning',
  'calendar.later': '{scene} in {days} working days',
  'calendar.label': 'Coming up',
  // The milestone (DEC-17 M1). The calendar says what is on Thursday; this says what Thursday is for.
  'milestone.label': 'Right now',
  // The meeting-notes bot's opening line for a crossed gate (DEC-17 M1). It names the milestone and
  // hands over to the write-up; it never says how the session is going.
  'milestone.post': 'We hit {gate}. {headline}',
  'milestone.next': 'next: {next}',
  'milestone.nextOpens': 'next: {next} — opens {unlocks}',
  // What the gate actually takes, so the strip stops being a signpost with no distance on it.
  'milestone.needs': 'gets there when {needs}',
  // §2j: a careful colleague visibly typing reads as personality; the same pause with nothing on
  // screen reads as a hung app.
  'typing.one': '{name} is typing…',
  'corridor.unread': '{n} new',
  'corridor.newSince': 'New since you were last here',
} as const;

export type MessageKey = keyof typeof EN;

/**
 * The Russian catalog. Complete rather than partial, for the same reason the terminal's is: a
 * half-translated screen is the state this work exists to remove, and the EN fallback would hide the
 * gap from every test. The Russian is written as Russian office speech, not translated word for word -
 * a workplace fiction that reads as a translation is not the fiction.
 */
const RU: Partial<Record<MessageKey, string>> = {
  'app.title': 'Симулятор команды',
  'app.workspaceFallback': 'команда',
  'app.channels': 'Каналы',
  'app.people': 'Люди',
  'app.pages': 'Страницы',
  'app.you': 'вы',
  'app.signIn': 'Войти в команду',
  'app.signInName': 'Как вас называть?',
  'app.signInGo': 'Заходите',
  'app.signingIn': 'Секунду…',
  'app.entryTitle': 'Team Simulator',
  'app.myWorkspaces': 'Ваши команды',
  'app.openWorkspaces': 'Команды, куда можно зайти',
  'app.noWorkspaces': 'Пока нет команд. Создайте свою ниже — получите ссылку для приглашения.',
  'app.newWorkspace': 'Собрать новую команду',
  'app.newWorkspaceName': 'Название команды',
  'app.newWorkspaceDeck': 'Во что вы входите',
  'app.deckExcerpt': '{title} — ситуация уже идёт, примерно {days} дней',
  'app.deckSim': '{title} — компания, которой вы управляете, пока получается',
  'app.enter': 'Открыть',
  'app.reconnecting': 'Восстанавливаем связь — читать можно, отправить пока нет.',
  'app.offlineNote': 'Делать ничего не нужно: связь вернётся сама.',
  'app.leave': 'Выйти',
  'app.problemSend': 'Не ушло. Скажите ещё раз, когда связь вернётся.',
  'outbox.sending': 'Отправляется…',
  'outbox.failed': 'Не ушло.',
  'outbox.retry': 'Отправить снова',
  'outbox.discard': 'Убрать',
  'app.problemJoin':
    'Не получилось зайти: {error}. Обновите список или попросите ссылку у того, кто вас позвал.',
  'app.problemCreate':
    'Не получилось собрать команду: {error}. Попробуйте ещё раз через мгновение.',
  'app.problemSignIn': 'Не получилось войти: {error}. Попробуйте ещё раз.',
  'app.problemSignedOut': 'Вы вышли. Назовите себя ещё раз, чтобы вернуться.',
  'app.problemWait': 'Слишком много попыток подряд — подождите {seconds} с и повторите.',
  'app.nameLooksLikeCommand':
    'Осторожно: «{name}» похоже на то, что вы хотели сделать, а не на имя – а это имя увидит вся команда.',
  'app.identityExists':
    'В этом браузере у вас уже есть имя, команд: {teams}. Новое начнёт всё заново, и его роли не вернуть. Оставьте поле пустым, чтобы вернуться собой.',
  'app.identityConfirm': 'Нажмите «Заходите» ещё раз, чтобы всё же использовать это имя.',
  'room.pickRole':
    'Вы ещё не взяли роль, поэтому за вас некому действовать и коллеги не могут вам ответить. Выберите роль в списке людей справа.',
  'room.watching':
    'Вы наблюдаете за этой комнатой. Можно всё читать и писать сюда; действовать нельзя, и коллеги не смогут вам ответить. Роль освободится, когда кто-нибудь уйдёт.',
  'app.noticeNoRole':
    'Вам пока некому ответить: вы не взяли роль. Выберите её в списке людей справа, и она ваша.',
  'app.noticeNoRoleFree':
    'Вам пока некому ответить: вы не взяли роль. Сейчас свободны: {roles}. Выберите роль в списке людей справа.',
  'app.noticeNoRoleFull':
    'Вам пока некому ответить: вы не взяли роль, а все занятия здесь уже разобраны. Скажите это всей комнате или откройте свою.',
  'app.noticeSeated': 'Вы теперь {role}.',
  'app.noticeObserver':
    'Свободных ролей не было, поэтому вы наблюдаете за этой комнатой. Можно всё читать и писать сюда; действовать нельзя. Откройте свою комнату, чтобы играть.',
  'app.noticeUnknownName': 'Здесь никого так не зовут. В этом канале можно обратиться к: {names}.',
  'app.noticeUnknownNameNobody':
    'Здесь никого так не зовут, и в этом канале никто не отвечает на обращения. Попробуйте общий канал.',
  'app.noticeTooFast':
    'Вы обращаетесь к людям быстрее, чем они успевают отвечать. Подождите {seconds} с и повторите.',
  'app.problemAction': '{message}',
  'app.problemRefused': 'Не прошло: {message}',

  'lobby.title': 'До первого утра',
  'lobby.subtitle': 'Люди ещё подходят. Поздоровайтесь и возьмите роль, которая вам нравится.',
  'lobby.roleOpen': 'Роль свободна',
  'lobby.roleTake': 'Взять',
  'lobby.roleTaken': 'Здесь {name}',
  'lobby.roleLocked': 'Сюда нужен тот, кто уже что-то здесь выпускал',
  'lobby.roleDoes': 'Делает: {powers}',
  'lobby.start': 'Начать день',
  'lobby.startHint': 'День начинает тот, кто собрал комнату. Остальные пока могут поговорить.',
  'lobby.waiting': 'Ждём комнату',
  'lobby.quiet': '{name} зашёл, но пока ничего не сказал',
  'lobby.ready': 'на месте',

  'composer.team': 'Говорите что угодно',
  'composer.dm': 'Спросите {name} о чём угодно',
  'composer.warRoom': 'Отвечайте здесь или нажмите кнопку под инцидентом',
  'composer.readOnly': 'Вы читаете. Возьмите роль, чтобы участвовать.',
  'composer.send': 'Отправить',
  'mention.label': 'Кого можно упомянуть',
  'mention.none': 'Здесь никто не подходит под «{prefix}»',

  'channel.team': '#team',
  'channel.teamPurpose': 'Команда. Настроение, разговоры, кто чем занят.',
  'channel.warRoom': '#war-room',
  'channel.warRoomSev': '#war-room · sev{n}',
  'channel.warRoomPurpose': 'Здесь тушат пожар.',
  'channel.dm': '{name}',

  'topic.goalGood': 'Закончим, когда: {win}',
  'topic.goalHold': 'Закончим, когда: {win} · держим: {lose}',
  'topic.goalBreached': '⚠ {lose} — это та черта, которую мы обещали не переходить',
  'topic.deadlineToday': 'Сегодня последний день.',
  'topic.deadlineDays': 'Осталось рабочих дней: {n}.',
  'topic.noDeadline': 'Дата не назначена.',
  'topic.clauseAbove': '{metric} на {current}, нужно {target}',
  'topic.clauseBelow': '{metric} на {current}, держим выше {target}',
  'topic.clauseUnder': '{metric} на {current}, нужно ниже {target}',
  'topic.clauseKeepUnder': '{metric} на {current}, держим ниже {target}',
  'topic.clauseAt': '{metric} на {current}, нужно {target}',
  'topic.and': ' и ',
  'topic.or': ' или ',

  'metric.stability': 'платформа',
  'metric.sev': 'тяжесть',
  'metric.trust': 'клиент',
  'metric.morale': 'команда',
  'metric.quality': 'качество',
  'metric.scope': 'объём',

  'page.monitoring': 'Мониторинг',
  'page.monitoringLead': 'Что показывают графики прямо сейчас.',
  'page.monitoringStability': 'Здоровье платформы',
  'page.monitoringSev': 'Тяжесть',
  'page.monitoringArmed': 'Под наблюдением: {metric}',
  'page.monitoringArmedUnknown': 'по этому показателю что-то запланировано',
  'page.pulse': 'Опрос настроения',
  'page.pulseLead': 'Те два нажатия, которые все делают в конце дня. Вот что вернулось.',
  'page.pulseScore': 'Как команда сама оценивает, что происходит',
  'page.pulseParticipation': 'Ответили сегодня: {done} из {total}',
  'page.accounts': 'Состояние клиента',
  'page.accountsLead': 'Что клиент думает о нас.',
  'page.accountsCustomer': 'Acme',
  'page.accountAtRisk': 'ПОД УГРОЗОЙ',
  'page.accountWatch': 'ПРИСМОТРЕТЬСЯ',
  'page.accountHealthy': 'В ПОРЯДКЕ',
  'page.rebuildingSlow': 'Восстанавливается медленно. Как всегда.',
  'page.empty': 'На этой странице пока пусто.',

  'gesture.signOff': 'Закругляюсь',
  'gesture.signOffHint': 'Напишет строчку, погасит вас и закроет ваш день.',
  'gesture.signOffLine': 'Всё, на сегодня закругляюсь.',
  'gesture.signedOff': 'Закруглился',
  'gesture.moodAsk': 'Пока не ушли — как оно было?',
  'gesture.moodYou': 'Вы',
  'gesture.moodTeam': 'Команда',
  'gesture.moodGood': 'Хорошо',
  'gesture.moodFlat': 'Никак',
  'gesture.moodRough': 'Тяжело',
  'gesture.moodSkip': 'Лучше промолчу',
  'presence.here': 'на месте',
  'presence.away': 'отошёл',
  'presence.gone': 'ушёл',
  'time.hover': 'На самом деле {real}',
  'day.divider': '{day}',
  'day.today': '{day} · сегодня',
  'day.names.1': 'Понедельник',
  'day.names.2': 'Вторник',
  'day.names.3': 'Среда',
  'day.names.4': 'Четверг',
  'day.names.5': 'Пятница',
  'day.week': '{n}-я неделя',

  'incident.sender': 'мониторинг',
  'incident.headline': '{text}',
  'incident.onlyYou': 'Это видите только вы: {reveal}',
  'incident.pick': 'Что делаем?',
  'incident.bet': 'Ставка — {odds}',
  'incident.unlabelled': 'другое',
  'incident.caused': 'Это следствие того, что команда уже решила.',
  'incident.scripted': 'Это должно было случиться.',
  'incident.needRole': 'Вы это читаете. Возьмите роль — и сможете ответить.',
  'confirm.title': 'Прежде чем ответить',
  'confirm.option': 'Вы выбираете: {label}',
  'confirm.moves': 'По чему это будут мерить:',
  'confirm.why': 'Одной строкой — почему. Команда потом это перечитает',
  'confirm.whyPlaceholder': 'потому что…',
  'confirm.whyRequired': 'Сначала строчку. О таких решениях потом обязательно спрашивают.',
  'confirm.go': 'Делаем',
  'confirm.cancel': 'Пока нет',

  'verb.title': 'Ваше',
  'verb.declare-incident': 'Объявить инцидент',
  'verb.talk-to-stakeholder': 'Позвонить клиенту',
  'verb.investigate': 'Разобраться',
  'verb.mitigate': 'Прикрыть',
  'verb.fix': 'Починить по-настоящему',
  'verb.update-status': 'Написать статус',
  'verb.generic': '{verb}',
  'verb.done': 'Сделано: {label}',

  'member.you': '{name} (вы)',
  'member.openRole': 'Свободная роль',
  'member.take': 'Взять эту роль',
  'member.takeRecommended': 'Взять эту роль — начните отсюда',
  'member.leaving': 'Отдать роль',

  'dm.heard': '«{quote}» — да.',
  'dm.scripted': 'по сценарию',
  'empty.dmColleague': 'Спросите {name} о чём-нибудь. Они говорят только о том, что знают.',
  'dm.deflect': 'Извините, тут не помогу. Спросите, как я, или про решение на столе.',
  'dm.thinking': 'печатает…',
  'channel.decisions': '#решения',
  'channel.decisionsPurpose': 'Что ждёт решения команды. У каждого есть владелец.',
  'empty.decisions': 'Сейчас ничего не ждёт решения.',
  'composer.decisions': 'Сказать что-то по этим вопросам',
  'decision.card': '{kind} — мне нужен ответ к дню {day}.',
  'decision.style.OwnerDecides': 'Решаю я. Скажите, если не согласны.',
  'decision.style.ConsultThenDecide': 'Хочу услышать {n} из вас, прежде чем решу.',
  'decision.style.Committee': 'Голосуем. Пока {votes}/{quorum}.',
  'decision.style.ApprovalChain': 'Нужна подпись: {approver}.',
  'decision.vote': 'Голос за {option}',
  'decision.decide': 'Выбрать {option}',
  'decision.approve': 'Подписать',
  'decision.defer': 'Не сегодня',
  'decision.deferredBefore': 'Уже откладывали: {n}.',
  'promise.offer': 'Пообещать {who}',
  'promise.title': 'Что вы обещаете?',
  'promise.what': 'Что вы сделаете',
  'promise.whatPlaceholder': 'дашборд по SLA',
  'promise.by': 'К какому дню',
  'promise.go': 'Даю слово',
  'promise.needWhat': 'Напишите, что именно обещаете — вам напомнят вашими же словами.',
  'finale.wonPlain': 'Вы это выпустили.',
  'finale.lostPlain': 'У команды кончилась дорога.',
  'finale.collapsedPlain': 'Компания не выжила.',
  'finale.survival': 'Как далеко зашло',
  'finale.survivalLasted': 'Продержались {days} дней.',
  'finale.survivalGates': 'Достигли вех: {gates}.',
  'finale.survivalPeak': 'В лучший момент — {peak}. В конце — {final}.',
  'finale.theRoom': 'команда',
  'finale.pathClock': 'никто не решил – решили часы',
  'finale.pathOverrode': 'решено вопреки возражениям: {n}',
  'finale.pathDeferred': 'до этого откладывали: {n}',
  'finale.allHands': 'Общий сбор',
  'finale.open': 'Открыть общий сбор',
  'finale.close': 'Вернуться в офис',
  'finale.next': 'Дальше',
  'finale.back': 'Назад',
  'finale.story': 'Что произошло',
  'finale.calls': 'Ваши решения',
  'finale.letter': 'Вам лично',
  'finale.scripted': 'Собрано по записям, без литературной обработки.',
  'finale.share': 'Скопировать, чтобы поделиться',
  'finale.shared': 'Скопировано.',
  'finale.score': 'Итог {score}',
  'finale.after': 'День закончен. Офис можно перечитать.',
  'channel.random': '#random',
  'channel.randomPurpose': 'Не про работу. Почти.',
  'composer.random': 'Написать не по делу',
  'empty.random': 'Тут тихо.',
  'cast.outsideWorld': 'Вне команды',
  'react.add': '+',
  'react.addHint': 'Отреагировать',
  'buddy.name': 'Нур Амрани',
  'buddy.role': 'Developer experience',
  'buddy.line1': 'Привет, добро пожаловать. Вы попали в {workspace} в плохое утро: у нас горит.',
  'buddy.line2':
    'Разговоры здесь настоящие. Пишите в любой канал, пишите кому угодно лично, спрашивайте меня — всё дойдёт.',
  'buddy.line3':
    'С решениями иначе. Они живут на кнопках под тем сообщением, где их подняли, а не в фразе, которую вы мне напишете.',
  'buddy.line4': 'Первое дело: ваша роль свободна, она в списке справа. Возьмите её.',
  'buddy.line4Seated': 'Роль у вас уже есть, так что всё готово. Идите поздоровайтесь в #team.',
  'buddy.started': 'Ну вот, день начался. Смотрите в #war-room.',
  'buddy.proseCommand':
    'Сказать так можно, но от этого оно не случится — это решение на инциденте в #war-room, на кнопках под ним.',
  'buddy.proseCommandIdle':
    'Сказать так здесь можно, но сейчас никто этого решения не ждёт. Когда будет ждать — оно появится в #war-room с кнопками.',
  'buddy.unknown':
    'Могу подсказать, где что: разговоры — в каналах, решения — на кнопках, ваша роль — в списке справа.',

  'empty.team': 'Ещё никто ничего не сказал. Можете вы.',
  'empty.warRoom': 'Тихо. Ненадолго.',
  'empty.dm': 'Только вы двое.',

  'beat.joined': '{name} в команде.',
  'beat.tookRole': '{name} теперь на роли {role}.',
  'beat.dayOpen': 'Доброе утро. {day}.',
  'beat.signedOff': '{name} закругляется.',
  'beat.narrator': 'комната',

  'bot.meeting': 'записи встреч',
  'bot.deploy': 'выкатки',
  'bot.digest': 'сводка дня',
  'bot.survey': 'опрос',

  'channel.standup': '#standup',
  'channel.standupPurpose': 'Встреча, пока она идёт.',
  'channel.standupOffered': 'Стоит в календаре. Пока никто не начал.',
  'composer.standup': 'Ответьте, чтобы участвовать',
  'empty.standup': 'Сейчас встречи нет.',
  'scene.standup': 'стендап',
  'scene.planning': 'планирование',
  'scene.governance': 'встреча по правилам',
  'scene.retro': 'ретро',
  'standup.opened': '{scene} стоит в календаре. Ответьте, когда будете готовы.',
  'standup.running': '{scene}, идёт сейчас.',
  'standup.take': 'Подключиться',
  'standup.next': 'Дальше',
  'standup.beatOf': 'часть {index} из {count}',
  'standup.beatDigest': 'Что произошло с прошлого раза.',
  'standup.beatReveals': 'Есть что-то, что остальным стоит знать?',
  'standup.beatAgenda': 'Что сегодня требует ответа.',
  'standup.beatCommitments': 'Кто что берёт.',
  'standup.beatClose': 'На этом всё. Как оно было?',
  'standup.digestQuiet': 'С прошлого раза ничего не произошло.',
  'standup.digestEvents': 'разобрано: {n}',
  'standup.digestDecisions': 'решений принято: {n}',
  'standup.digestTimedOut': 'решений ушло по сроку худшим путём: {n}',
  'standup.digestWork': 'выпущено: {n}',
  'standup.digestFuses': 'сработало из заложенного раньше: {n}',
  'standup.digestEscalated': 'опасений вышло наружу: {n}',
  'standup.digestPromises': 'обещаний нарушено: {n}',
  'standup.digestHires': 'новых людей вышло: {n}',

  'calendar.today': '{scene} сегодня',
  'calendar.tomorrow': '{scene} завтра утром',
  'calendar.later': '{scene} через {days} рабочих дня(ей)',
  'calendar.label': 'Ближайшее',
  'milestone.label': 'Сейчас',
  'milestone.post': 'Прошли этап «{gate}». {headline}',
  'milestone.next': 'дальше: {next}',
  'milestone.nextOpens': 'дальше: {next} — откроет {unlocks}',
  'milestone.needs': 'дойдём, когда {needs}',
  'typing.one': '{name} печатает…',
  'corridor.unread': 'новых: {n}',
  'corridor.newSince': 'Новое с вашего прошлого захода',
};

const CATALOGS: Record<Lang, Partial<Record<MessageKey, string>>> = { en: EN, ru: RU };

let active: Lang = 'en';

/** Sets the interface language. Anything that is not a known catalog falls back to English. */
export function setLanguage(lang: string): void {
  active = lang.toLowerCase().startsWith('ru') ? 'ru' : 'en';
}

/** The active interface language. */
export function language(): Lang {
  return active;
}

/** One translated line. Missing RU falls back to EN rather than to the key. */
export function t(key: MessageKey, params?: Record<string, string | number>): string {
  const text = CATALOGS[active][key] ?? EN[key];
  if (!params) {
    return text;
  }

  return text.replace(/\{(\w+)\}/g, (whole, name: string) =>
    name in params ? String(params[name]) : whole,
  );
}

/** Exported for the coverage + language-law tests. */
export const CATALOG_KEYS = Object.keys(EN) as MessageKey[];

/** Exported for the coverage + language-law tests. */
export const EN_CATALOG: Record<MessageKey, string> = EN;

/** Exported for the coverage + language-law tests. */
export const RU_CATALOG = RU;
